import { useState, useRef, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useDashboard } from "@/context/DashboardContext";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/hooks/useConfirm";
import axios from "axios";
import { API } from "@/App";
import ChanakyaIntelligencePanel from "@/components/ChanakyaIntelligencePanel";
import { useSessionTokens } from "@/hooks/useSessionTokens";
import {
  Send, Mic, MicOff, Check, X, Sparkles, ShoppingBag, Wallet,
  Target, TrendingUp, Pin, PinOff, Trash2, Reply,
  Search, Bell, Info, Zap,
} from "lucide-react";

// ── Chanakya DP — sage with a coin, warm amber gradient ───────────────────────
function ChanakyaDP({ size = 40 }) {
  const r = Math.round(size * 0.28);   // border-radius
  const fs = Math.round(size * 0.42);  // font size for ₹
  return (
    <div
      style={{
        width: size, height: size, borderRadius: r, flexShrink: 0,
        background: "linear-gradient(135deg,#92400e 0%,#b45309 40%,#d97706 100%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 2px 8px rgba(180,83,9,0.45)",
        position: "relative", overflow: "hidden",
      }}
    >
      {/* subtle ring */}
      <div style={{
        position: "absolute", inset: 0, borderRadius: r,
        border: "1.5px solid rgba(251,191,36,0.35)",
      }} />
      <span style={{ fontSize: fs, fontWeight: 900, color: "#fef3c7", lineHeight: 1, userSelect: "none" }}>
        ₹
      </span>
    </div>
  );
}

// ── Dynamic subtitle — cycles through feature prompts ─────────────────────────
const DYNAMIC_HINTS = [
  "Tell me what you spent today",
  "Log an EMI or loan payment",
  "How much did I save this month?",
  "Set a savings goal",
  "Track a recurring expense",
  "Can I afford a trip this weekend?",
  "Add income — salary, freelance, bonus",
  "Check my financial health score",
  "Log a gift or hand loan",
  "How's my EMI-to-income ratio?",
  "Start a piggy bank for something special",
  "What's my top spend this month?",
  // Income-specific
  "salary 85000",
  "Got freelance payment of 30000",
  "rental income 15000 this month",
  "received bonus 50000",
  "dividend 8000 from mutual funds",
  "How much income did I earn this year?",
  "What's my income vs expenses this month?",
];

function DynamicSubtitle() {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx(i => (i + 1) % DYNAMIC_HINTS.length);
        setVisible(true);
      }, 350);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  return (
    <p
      className="text-[11px] text-stone-400 mt-0.5 truncate transition-opacity duration-300"
      style={{ opacity: visible ? 1 : 0 }}
    >
      {DYNAMIC_HINTS[idx]}
    </p>
  );
}

// ── Quick action chips — always visible, tap to open inline form ──────────────
const QUICK_ACTIONS = [
  { key: "expense", icon: ShoppingBag, label: "+ Expense", color: "#f97316" },
  { key: "income",  icon: Wallet,      label: "+ Income",  color: "#10b981" },
  { key: "emi",     icon: TrendingUp,  label: "+ EMI",     color: "#6366f1" },
  { key: "goal",    icon: Target,      label: "+ Goal",    color: "#f59e0b" },
];

const EXPENSE_CATS = ["Food", "Transport", "Shopping", "Bills", "Health", "Entertainment", "Travel", "Other"];
const INCOME_TYPES = ["Salary", "Freelance", "Rental", "Bonus", "Dividend", "Other"];

// Inline composer — replaces text input when a quick action chip is tapped
function calcEMI(principal, annualRate, tenureMonths) {
  if (!annualRate) return Math.round(principal / tenureMonths);
  const r = annualRate / 12 / 100;
  return Math.round(principal * r * Math.pow(1 + r, tenureMonths) / (Math.pow(1 + r, tenureMonths) - 1));
}

function QuickComposer({ action, onSend, onClose }) {
  const [rows,      setRows]      = useState([{ amount: '', tag: '' }]);
  const [name,      setName]      = useState('');
  const [amount,    setAmount]    = useState('');
  const [principal, setPrincipal] = useState('');
  const [rate,      setRate]      = useState('');
  const [tenure,    setTenure]    = useState('');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  });
  const [confirmed, setConfirmed] = useState(false);
  const [sent,      setSent]      = useState(false);
  const amtRef = useRef(null);

  const isMulti = action === 'expense' || action === 'income';
  const cfg  = QUICK_ACTIONS.find(a => a.key === action);
  const cats = action === 'expense' ? EXPENSE_CATS : INCOME_TYPES;

  const resetForm = () => {
    setRows([{ amount: '', tag: '' }]);
    setName(''); setAmount(''); setPrincipal(''); setRate(''); setTenure('');
    setConfirmed(false); setSent(false);
    setTimeout(() => amtRef.current?.focus(), 80);
  };

  useEffect(() => { resetForm(); }, [action]);

  const updateRow = (idx, field, val) => setRows(r => r.map((x, i) => i === idx ? { ...x, [field]: val } : x));
  const addRow    = () => setRows(r => [...r, { amount: '', tag: '' }]);
  const removeRow = (idx) => setRows(r => r.filter((_, i) => i !== idx));

  const validRows = rows.filter(r => r.amount.trim() && r.tag);

  const emiAmt = action === 'emi' && principal && tenure
    ? calcEMI(parseFloat(principal), parseFloat(rate) || 0, parseInt(tenure))
    : 0;

  const canReview = isMulti
    ? validRows.length > 0
    : action === 'emi'  ? (!!name && !!principal && !!tenure)
    : action === 'goal' ? (!!name && !!amount.trim()) : false;

  const buildMsg = () => {
    if (action === 'expense') return validRows.map(r => `spent ${r.amount.trim()} on ${r.tag}`).join('\n');
    if (action === 'income')  return validRows.map(r => r.tag === 'Salary' ? `salary ${r.amount.trim()}` : `${r.tag.toLowerCase()} payment ${r.amount.trim()}`).join('\n');
    if (action === 'emi')     return `add home loan ${name} ${principal} at ${rate || '0'}% ${tenure} months starting ${startDate} monthly payment ${emiAmt}`;
    if (action === 'goal')    return `saving for ${name} ${amount.trim()}`;
    return '';
  };

  const summaryLines = () => {
    if (isMulti) return validRows.map((r, i) => `${i + 1}. ${r.tag} — ₹${parseFloat(r.amount).toLocaleString('en-IN')}`);
    if (action === 'emi') return [
      `Loan: ${name}`,
      `Principal: ₹${parseFloat(principal).toLocaleString('en-IN')}`,
      rate ? `Rate: ${rate}% p.a.` : 'Rate: 0% (interest-free)',
      `Tenure: ${tenure} months`,
      `Monthly EMI: ₹${emiAmt.toLocaleString('en-IN')}`,
      `Starts: ${startDate}`,
    ];
    if (action === 'goal') return [`Goal: ${name}`, `Target: ₹${parseFloat(amount).toLocaleString('en-IN')}`];
    return [];
  };

  const title = sent ? '✓ Done' : confirmed ? 'Confirm' :
    action === 'expense' ? 'Log Expenses' : action === 'income' ? 'Add Income' :
    action === 'emi' ? 'Add EMI' : 'New Goal';

  return (
    <div style={{ background: "rgba(28,25,23,0.97)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, padding: "14px 14px 12px", margin: "0 12px 8px" }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {cfg && <cfg.icon size={14} style={{ color: cfg.color }} />}
          <span className="text-xs font-semibold text-stone-200">{title}</span>
        </div>
        <button onClick={onClose} className="text-stone-500 hover:text-stone-300"><X size={14} /></button>
      </div>

      {/* ── Success screen ── */}
      {sent ? (
        <>
          <div className="flex flex-col items-center py-4 gap-2">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: (cfg?.color ?? '#f97316') + '22' }}>
              <span className="text-2xl">✓</span>
            </div>
            <p className="text-sm font-bold text-stone-100">Logged!</p>
            <p className="text-[11px] text-stone-500">Chanakya has recorded your {isMulti && validRows.length > 1 ? `${validRows.length} entries` : 'entry'}.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={resetForm} className="flex-1 py-2 rounded-xl text-xs font-semibold text-stone-400 border border-stone-700 hover:border-stone-500 transition-all">
              + Add more
            </button>
            <button onClick={onClose} className="flex-1 py-2 rounded-xl text-xs font-semibold text-white transition-all" style={{ background: cfg?.color }}>
              Done
            </button>
          </div>
        </>

      ) : confirmed ? (
        /* ── Confirm screen ── */
        <>
          <div className="rounded-2xl border border-white/8 p-3 mb-3" style={{ background: 'rgba(34,20,8,0.85)' }}>
            {summaryLines().map((line, i) => (
              <p key={i} className="text-xs text-stone-200 py-1 border-b border-white/5 last:border-0">{line}</p>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setConfirmed(false)} className="py-2 px-4 rounded-xl text-xs font-semibold text-stone-400 border border-stone-700 hover:border-stone-500 transition-all">
              Edit
            </button>
            <button
              onClick={() => { onSend(buildMsg()); setSent(true); }}
              className="flex-1 py-2 rounded-xl text-xs font-semibold text-white transition-all active:scale-[0.98]"
              style={{ background: cfg?.color }}
            >
              Confirm & Send
            </button>
          </div>
        </>

      ) : (
        /* ── Form screen ── */
        <>
          {isMulti ? (
            <div className="max-h-72 overflow-y-auto pr-0.5 mb-2" style={{ scrollbarWidth: 'thin', scrollbarColor: '#44403c transparent' }}>
              {rows.map((row, idx) => (
                <div key={idx} className="mb-2 rounded-2xl border border-white/10 p-3" style={{ background: 'rgba(34,20,8,0.85)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold border"
                        style={{ color: cfg?.color, borderColor: (cfg?.color ?? '#f97316') + '66', background: (cfg?.color ?? '#f97316') + '22' }}
                      >{idx + 1}</span>
                      <span className="text-[11px] text-stone-500 font-medium">Entry {idx + 1}</span>
                    </div>
                    {rows.length > 1 && (
                      <button onClick={() => removeRow(idx)} className="text-stone-600 hover:text-red-400 transition-colors"><X size={14} /></button>
                    )}
                  </div>
                  <div className="relative mb-2">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm font-medium">₹</span>
                    <input
                      ref={idx === 0 ? amtRef : null}
                      type="number" inputMode="numeric" value={row.amount}
                      onChange={e => updateRow(idx, 'amount', e.target.value)}
                      placeholder="Amount"
                      className="w-full bg-stone-800/80 border border-stone-700/40 text-stone-100 placeholder-stone-500 text-sm rounded-xl pl-7 pr-3 py-2 outline-none focus:border-amber-600/50"
                      onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
                    />
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {cats.map(c => (
                      <button key={c} onClick={() => updateRow(idx, 'tag', c)}
                        className="px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all"
                        style={row.tag === c
                          ? { background: cfg?.color, borderColor: cfg?.color, color: '#fff' }
                          : { background: 'transparent', borderColor: 'rgba(255,255,255,0.12)', color: '#a8a29e' }}
                      >{c}</button>
                    ))}
                  </div>
                  {!row.tag && <p className="text-[10px] text-stone-600 mt-1.5">← Pick a category</p>}
                </div>
              ))}
              <button onClick={addRow}
                className="w-full py-2.5 rounded-2xl border border-dashed border-white/10 text-xs font-semibold flex items-center justify-center gap-1.5 hover:border-white/20 mb-1 transition-colors"
                style={{ color: cfg?.color }}
              >+ Add another entry</button>
            </div>
          ) : (
            <>
              <input ref={amtRef} value={name} onChange={e => setName(e.target.value)}
                placeholder={action === 'emi' ? 'Loan name (e.g. Home Loan, Car Loan)' : 'Goal name (e.g. iPhone, Vacation)'}
                className="w-full bg-stone-800/80 border border-stone-700/40 text-stone-100 placeholder-stone-500 text-sm rounded-xl px-3 py-2 outline-none focus:border-amber-600/50 mb-2"
              />
              <div className="relative mb-2">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm font-medium">₹</span>
                <input type="number" inputMode="numeric"
                  value={action === 'emi' ? principal : amount}
                  onChange={e => action === 'emi' ? setPrincipal(e.target.value) : setAmount(e.target.value)}
                  placeholder={action === 'emi' ? 'Principal amount' : 'Target amount'}
                  className="w-full bg-stone-800/80 border border-stone-700/40 text-stone-100 placeholder-stone-500 text-sm rounded-xl pl-7 pr-3 py-2 outline-none focus:border-amber-600/50"
                />
              </div>
              {action === 'emi' && (
                <>
                  <div className="flex gap-2 mb-2">
                    <input type="number" value={rate} onChange={e => setRate(e.target.value)}
                      placeholder="Rate % p.a."
                      className="flex-1 bg-stone-800/80 border border-stone-700/40 text-stone-100 placeholder-stone-500 text-xs rounded-xl px-3 py-2 outline-none focus:border-amber-600/50" />
                    <input type="number" value={tenure} onChange={e => setTenure(e.target.value)}
                      placeholder="Tenure (months)"
                      className="flex-1 bg-stone-800/80 border border-stone-700/40 text-stone-100 placeholder-stone-500 text-xs rounded-xl px-3 py-2 outline-none focus:border-amber-600/50" />
                  </div>
                  <input value={startDate} onChange={e => setStartDate(e.target.value)}
                    placeholder="Start date (YYYY-MM)"
                    className="w-full bg-stone-800/80 border border-stone-700/40 text-stone-100 placeholder-stone-500 text-xs rounded-xl px-3 py-2 outline-none focus:border-amber-600/50 mb-2"
                  />
                  {emiAmt > 0 && (
                    <p className="text-xs font-bold text-emerald-400 text-center mb-2">
                      Calculated EMI: ₹{emiAmt.toLocaleString('en-IN')} / month
                    </p>
                  )}
                </>
              )}
            </>
          )}

          {/* Review button */}
          <button
            onClick={() => canReview && setConfirmed(true)}
            disabled={!canReview}
            style={canReview ? { background: cfg?.color } : {}}
            className="w-full py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-30 disabled:bg-stone-700 transition-all active:scale-[0.98]"
          >
            {isMulti && validRows.length > 1 ? `Review ${validRows.length} Entries →` : 'Review →'}
          </button>
        </>
      )}
    </div>
  );
}

// ── Bold/link/line-break renderer ─────────────────────────────────────────────
function renderMessage(text) {
  // Split on bold (**text** or *text*) and markdown links [label](url)
  const TOKEN = /(\*\*?[^*]+\*\*?|\[[^\]]+\]\([^)]+\))/g;
  const parts = text.split(TOKEN);
  return parts.map((part, i) => {
    // Bold: **text** or *text*
    if (/^\*\*?.+\*\*?$/.test(part)) {
      const inner = part.replace(/^\*+|\*+$/g, "");
      return <strong key={i} className="font-semibold text-amber-300">{inner}</strong>;
    }
    // Markdown link: [label](url)
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      const [, label, url] = linkMatch;
      const isInternal = url.startsWith("/");
      return (
        <a key={i} href={url}
          target={isInternal ? "_blank" : "_blank"}
          rel="noopener noreferrer"
          className="text-amber-400 underline underline-offset-2 hover:text-amber-300 font-semibold">
          {label}
        </a>
      );
    }
    // Plain text — preserve line breaks
    return part.split("\n").map((line, j, arr) => (
      <span key={`${i}-${j}`}>{line}{j < arr.length - 1 && <br />}</span>
    ));
  });
}

const CHANAKYA_ACTIONS = [
  '📖 Reading your financial profile',
  '💸 Scanning your expenses',
  '🏦 Checking your EMIs',
  '🎯 Looking at your goals',
  '🧮 Crunching the numbers',
  '📊 Analyzing your spend patterns',
  '💡 Connecting the dots',
  '🔍 Reviewing your history',
  '⚡ Thinking hard',
  '🌿 Calculating safe-to-spend',
];

function TypingDots() {
  const [idx, setIdx] = useState(0);
  const [fading, setFading] = useState(false);
  useEffect(() => {
    const id = setInterval(() => {
      setFading(true);
      setTimeout(() => { setIdx(i => (i + 1) % CHANAKYA_ACTIONS.length); setFading(false); }, 250);
    }, 2200);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="flex flex-col gap-2 px-1 py-1">
      <span className="text-[13px] text-stone-400 transition-opacity duration-[250ms]" style={{ opacity: fading ? 0 : 1 }}>
        {CHANAKYA_ACTIONS[idx]}
      </span>
      <div className="flex items-center gap-1.5">
        {[0, 1, 2].map(i => (
          <span key={i} className="w-1.5 h-1.5 rounded-full bg-amber-400"
            style={{ animation: `ckBounce 0.84s ease-in-out ${i * 0.14}s infinite` }} />
        ))}
      </div>
      <style>{`@keyframes ckBounce{0%,100%{transform:translateY(0) scale(1);opacity:0.4}50%{transform:translateY(-6px) scale(1.3);opacity:1}}`}</style>
    </div>
  );
}

// ── Client-side sensitive-data guard (fires BEFORE server call) ───────────────
// Mirrors backend patterns — any match blocks the message immediately.
const SENSITIVE_RULES = [
  { re: /\b(?:\d[ \-]?){15}\d\b/,                                              label: "card number"          },
  { re: /\b(?:cvv|cvc|security\s*code)[\s:=]+\d{3,4}\b/i,                      label: "CVV"                  },
  { re: /\b(?:atm\s*pin|upi\s*pin|mpin|m-?pin|transaction\s*pin)[\s:=]+\d{4,6}\b/i, label: "PIN"             },
  { re: /\b(?:otp|one[\s\-]?time\s*(?:password|code))[\s:=]+\d{4,8}\b/i,       label: "OTP"                  },
  { re: /\b(?:password|passwd|net\s*banking\s*pass(?:word)?)[\s:=]+\S+/i,       label: "password"             },
  { re: /\b[A-Z]{5}[0-9]{4}[A-Z]\b/,                                            label: "PAN number"           },
  { re: /\b\d{4}[\s\-]?\d{4}[\s\-]?\d{4}\b/,                                   label: "Aadhaar number"       },
  { re: /\b(?:account\s*(?:no|number|num)[\s:#]*)\d{9,18}\b/i,                  label: "bank account number"  },
  { re: /\b[A-Z]{4}0[A-Z0-9]{6}\b/,                                             label: "IFSC code"            },
  { re: /\b(?:user\s*(?:id|name)|customer\s*id|login\s*id)[\s:=]+\S+/i,         label: "net banking username" },
  { re: /\b(?:demat\s*(?:account|no)?[\s:=]*|dp\s*id[\s:=]+)\d{8,16}\b/i,      label: "demat account"        },
  { re: /\b[A-Z][1-9][0-9]{7}\b/,                                               label: "passport number"      },
  { re: /\b(?:secret\s*(?:key|answer)|security\s*question)[\s:=]+\S+/i,         label: "security credential"  },
];

function detectSensitiveData(text) {
  for (const { re, label } of SENSITIVE_RULES) {
    if (re.test(text)) return label;
  }
  return null;
}

// ── Voice hook ────────────────────────────────────────────────────────────────
function useSpeechRecognition(onResult) {
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.lang = "en-IN"; r.interimResults = false;
    r.onresult = (e) => { onResult(e.results[0][0].transcript); setListening(false); };
    r.onerror  = () => setListening(false);
    r.onend    = () => setListening(false);
    recRef.current = r;
  }, [onResult]);
  const toggle = () => {
    if (!recRef.current) return;
    if (listening) { recRef.current.stop(); setListening(false); }
    else           { recRef.current.start(); setListening(true); }
  };
  return { listening, toggle, supported: !!recRef.current };
}

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const CHAT_CACHE_KEY = "bm_chat_msgs";

// ── Info modal — transparency card from /chatbot/usage-info ──────────────────
function InfoModal({ onClose }) {
  const [data, setData]       = useState(null);
  const [err,  setErr]        = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/chatbot/usage-info`, auth())
      .then(r => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(() => { if (alive) { setErr("Couldn't load usage info."); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.6)" }}
      onClick={onClose}
    >
      <div
        className="bg-stone-900 border border-stone-700/60 rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto scrollbar-none"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-800 sticky top-0 bg-stone-900">
          <h3 className="text-sm font-bold text-stone-100 flex items-center gap-2">
            <Info size={15} className="text-amber-400" /> Chanakya usage & data
          </h3>
          <button onClick={onClose} className="text-stone-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {loading && (
          <div className="px-4 py-6 text-center text-stone-400 text-sm">Loading…</div>
        )}
        {err && (
          <div className="px-4 py-6 text-center text-rose-400 text-sm">{err}</div>
        )}
        {data && (
          <div className="px-4 py-3 space-y-4 text-sm">
            <div className="bg-stone-800/60 border border-stone-700/40 rounded-xl p-3">
              <p className="text-stone-100 font-semibold mb-0.5">
                Today: {data.usage?.today ?? 0} / {data.usage?.daily_limit ?? 30} messages
              </p>
              <p className="text-xs text-stone-400">
                This month: {data.usage?.this_month ?? 0} • Lifetime: {data.usage?.lifetime ?? 0}
              </p>
            </div>

            <div className="bg-emerald-950/40 border border-emerald-800/40 rounded-xl p-3">
              <p className="text-emerald-300 text-xs font-semibold">
                Credits spent: {data.credits?.spent_lifetime ?? 0} — chat is free
              </p>
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-wide text-stone-500 font-semibold mb-2">
                Data Chanakya sees during chats:
              </p>
              <div className="space-y-2">
                {(data.data_shared_with_ai || []).map((row, i) => (
                  <div key={i} className="flex items-start justify-between gap-3 bg-stone-800/50 border border-stone-700/30 rounded-lg px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-stone-200 text-xs font-semibold">{row.label}</p>
                      {row.detail && <p className="text-[11px] text-stone-400 mt-0.5">{row.detail}</p>}
                    </div>
                    <span className="text-xs font-bold text-amber-400 shrink-0">
                      {row.count == null ? "—" : row.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {(data.notes || []).length > 0 && (
              <ul className="list-disc pl-5 space-y-1 text-[11px] text-stone-400">
                {data.notes.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Daily Brief component — Autonomous Financial Agent ───────────────────────
const INSIGHT_COLORS = {
  alert:    { border: "rgba(239,68,68,0.35)",  bg: "rgba(239,68,68,0.08)",  text: "#fca5a5" },
  warning:  { border: "rgba(245,158,11,0.35)", bg: "rgba(245,158,11,0.08)", text: "#fcd34d" },
  nudge:    { border: "rgba(99,102,241,0.35)", bg: "rgba(99,102,241,0.08)", text: "#a5b4fc" },
  win:      { border: "rgba(16,185,129,0.35)", bg: "rgba(16,185,129,0.08)", text: "#6ee7b7" },
  reminder: { border: "rgba(249,115,22,0.35)", bg: "rgba(249,115,22,0.08)", text: "#fdba74" },
};

const STATUS_COLORS = {
  excellent:       { bg: "rgba(16,185,129,0.15)", text: "#6ee7b7", border: "rgba(16,185,129,0.3)" },
  good:            { bg: "rgba(99,102,241,0.15)", text: "#a5b4fc", border: "rgba(99,102,241,0.3)" },
  needs_attention: { bg: "rgba(245,158,11,0.15)", text: "#fcd34d", border: "rgba(245,158,11,0.3)" },
  critical:        { bg: "rgba(239,68,68,0.15)",  text: "#fca5a5", border: "rgba(239,68,68,0.3)"  },
};

function DailyBrief() {
  const [expanded,  setExpanded]  = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [brief,     setBrief]     = useState(null);
  const [error,     setError]     = useState(null);

  const fetchBrief = async () => {
    if (brief) { setExpanded(true); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/agent/daily-brief`, auth());
      setBrief(res.data);
      setExpanded(true);
    } catch {
      setError("Couldn't load your brief. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const statusColor = brief ? (STATUS_COLORS[brief.status] || STATUS_COLORS.good) : null;
  const scoreColor  = brief ? (
    brief.score >= 85 ? "#6ee7b7" : brief.score >= 65 ? "#a5b4fc" : brief.score >= 40 ? "#fcd34d" : "#fca5a5"
  ) : "#f97316";

  return (
    <div className="mx-4 mb-2 shrink-0">
      {/* Collapsed trigger / header */}
      <button
        onClick={() => brief ? setExpanded(e => !e) : fetchBrief()}
        className="w-full flex items-center justify-between px-4 py-2.5 rounded-2xl border transition-all active:scale-[0.99]"
        style={{
          background: "rgba(28,25,23,0.90)",
          borderColor: brief ? (statusColor?.border || "rgba(255,255,255,0.08)") : "rgba(255,255,255,0.08)",
        }}
      >
        <div className="flex items-center gap-2.5">
          <Sparkles size={14} style={{ color: "#f97316" }} />
          <span className="text-xs font-semibold text-stone-200">Daily Brief</span>
          {brief && (
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: statusColor?.bg, color: statusColor?.text, border: `1px solid ${statusColor?.border}` }}
            >
              {brief.status.replace("_", " ")}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {brief && (
            <span className="text-base font-extrabold" style={{ color: scoreColor }}>
              {brief.score}
            </span>
          )}
          {loading
            ? <span className="text-[10px] text-stone-500 animate-pulse">Loading…</span>
            : !brief
              ? <span className="text-[10px] text-orange-400 font-semibold">Tap to brief me</span>
              : <span className="text-stone-600" style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)", display: "inline-block", transition: "transform 0.2s" }}>▾</span>
          }
        </div>
      </button>

      {error && (
        <p className="text-[11px] text-rose-400 text-center mt-1">{error}</p>
      )}

      {/* Expanded content */}
      {expanded && brief && (
        <div
          className="mt-1.5 rounded-2xl border overflow-hidden"
          style={{ background: "rgba(28,25,23,0.95)", borderColor: "rgba(255,255,255,0.07)" }}
        >
          {/* Score row */}
          <div className="flex items-center gap-4 px-4 py-3 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
            <div className="text-center">
              <p className="text-3xl font-extrabold leading-none" style={{ color: scoreColor }}>{brief.score}</p>
              <p className="text-[9px] text-stone-500 mt-0.5 uppercase tracking-wide">Score</p>
            </div>
            <div className="flex-1 grid grid-cols-2 gap-x-4 gap-y-0.5">
              <p className="text-[10px] text-stone-400">Income <span className="text-stone-200 font-semibold">₹{brief.snapshot.income_this_month.toLocaleString('en-IN')}</span></p>
              <p className="text-[10px] text-stone-400">Spent <span className="text-stone-200 font-semibold">₹{brief.snapshot.spent_this_month.toLocaleString('en-IN')}</span></p>
              <p className="text-[10px] text-stone-400">EMIs <span className="text-stone-200 font-semibold">{brief.snapshot.active_emis}</span></p>
              <p className="text-[10px] text-stone-400">Goals <span className="text-stone-200 font-semibold">{brief.snapshot.active_goals}</span></p>
            </div>
          </div>

          {/* Insights */}
          <div className="divide-y" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
            {brief.insights.map((ins, i) => {
              const ic = INSIGHT_COLORS[ins.type] || INSIGHT_COLORS.win;
              return (
                <div key={i} className="px-4 py-2.5 flex items-start gap-2.5"
                  style={{ background: ic.bg }}>
                  <span className="text-base leading-none mt-0.5 shrink-0">{ins.icon}</span>
                  <div className="min-w-0">
                    <p className="text-[12px] font-semibold" style={{ color: ic.text }}>{ins.title}</p>
                    <p className="text-[11px] text-stone-400 mt-0.5 leading-relaxed">{ins.body}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 flex items-center justify-between border-t" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
            <p className="text-[10px] text-stone-600">{brief.month}</p>
            <button
              onClick={() => { setBrief(null); setExpanded(false); fetchBrief(); }}
              className="text-[10px] text-stone-500 hover:text-amber-400 transition-colors"
            >
              Refresh ↻
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const Chatbot = () => {
  const { prefetch } = useDashboard();
  const { user, refreshUser } = useAuth();
  const confirm = useConfirm();
  const { totals: sessionTokens, addTokens } = useSessionTokens();
  const sendMessageRef = useRef(null);
  const [messages,       setMessages]       = useState([]);
  const [input,          setInput]          = useState("");
  const [preOptInput,    setPreOptInput]    = useState(null); // snapshot before optimize
  const [isLoading,      setIsLoading]      = useState(false);
  const [historyLoading, setHistoryLoading] = useState(() => {
    // Start as false if we have cached messages — no loading spinner needed
    try { return !JSON.parse(localStorage.getItem(CHAT_CACHE_KEY) || "[]").length; }
    catch { return true; }
  });
  const [pendingEntries, setPendingEntries] = useState(null);
  const [pendingDelete,  setPendingDelete]  = useState(null);   // {transaction_id, description, amount}
  const [pendingEdit,    setPendingEdit]    = useState(null);   // {transaction_id, proposed: {...}}
  const [replyTo,        setReplyTo]        = useState(null);   // {id, role, content}
  const [activeAction,   setActiveAction]   = useState(null);   // 'expense'|'income'|'emi'|'goal'|null
  const [searchMode,     setSearchMode]     = useState(false);
  const [searchQuery,    setSearchQuery]    = useState("");
  const [searchResults,  setSearchResults]  = useState(null);
  const [pinnedOpen,     setPinnedOpen]     = useState(false);
  const [pinned,         setPinned]         = useState([]);
  const [hasMore,        setHasMore]        = useState(true);
  const [loadingMore,    setLoadingMore]    = useState(false);
  const [infoOpen,       setInfoOpen]       = useState(false);
  const [prefSaving,     setPrefSaving]     = useState(false);

  // ── Updates tab ───────────────────────────────────────────────────────────
  const [activeTab,      setActiveTab]      = useState('chat'); // 'chat' | 'updates'
  const [notifs,         setNotifs]         = useState([]);
  const [notifsLoading,  setNotifsLoading]  = useState(false);
  const [unreadCount,    setUnreadCount]    = useState(0);

  const fetchNotifs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/inbox`, auth());
      setNotifs(res.data?.items || []);
      setUnreadCount(res.data?.unread_count || 0);
    } catch {}
    finally { setNotifsLoading(false); }
  }, []);

  useEffect(() => { fetchNotifs(); }, [fetchNotifs]);

  // ── Chat preferences (AI on/off, language) ────────────────────────────────
  const aiEnabled = user?.ai_consent_enabled !== false; // default true
  const lang      = user?.chat_language_pref === 'english' ? 'english' : 'hinglish';

  const updatePref = useCallback(async (patch) => {
    if (prefSaving) return;
    setPrefSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, patch, auth());
      await refreshUser?.();
    } catch { /* ignore — keep previous value */ }
    finally { setPrefSaving(false); }
  }, [prefSaving, refreshUser]);

  const toggleAI   = () => updatePref({ ai_consent_enabled: !aiEnabled });
  const toggleLang = () => updatePref({ chat_language_pref: lang === 'english' ? 'hinglish' : 'english' });

  const messagesEndRef  = useRef(null);
  const inputRef        = useRef(null);
  const scrollRef       = useRef(null);
  const oldestIdRef     = useRef(null);
  const containerRef    = useRef(null);

  // ── Mobile keyboard: push container up when keyboard opens ────────────────
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      if (!containerRef.current || window.innerWidth >= 768) return;
      const keyboardH = window.innerHeight - vv.offsetTop - vv.height;
      containerRef.current.style.bottom = keyboardH > 100 ? `${keyboardH}px` : '';
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  // ── Prefetch dashboard data in background while chat loads ────────────────
  useEffect(() => { prefetch(); }, [prefetch]);

  // ── Prefill from query param (e.g. /chat?prefill=How+do+I+save+more) ──
  const [searchParams, setSearchParams] = useSearchParams();
  const prefillHandled = useRef(false);
  useEffect(() => {
    if (prefillHandled.current) return;
    const q = searchParams.get("prefill");
    if (q) {
      prefillHandled.current = true;
      setInput(q);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // ── Load history — show cache instantly, refresh from server in background ──
  useEffect(() => {
    // Step 1: render cached messages immediately (zero wait)
    try {
      const cached = JSON.parse(localStorage.getItem(CHAT_CACHE_KEY) || "[]");
      if (cached.length) {
        setMessages(cached);
        if (cached[0]?.id) oldestIdRef.current = cached[0].id;
      }
    } catch { /* ignore */ }

    // Step 2: fetch fresh history from server in background
    const loadHistory = async () => {
      try {
        const res = await axios.get(`${API}/chatbot/history?limit=20`, auth());
        const msgs = res.data || [];
        if (msgs.length < 20) setHasMore(false);
        if (msgs.length > 0) oldestIdRef.current = msgs[0].id;

        if (msgs.length === 0) {
          setMessages([onboardingMsg()]);
          localStorage.removeItem(CHAT_CACHE_KEY);
        } else {
          setMessages(msgs);
          try { localStorage.setItem(CHAT_CACHE_KEY, JSON.stringify(msgs.slice(-20))); } catch { /* quota */ }
        }
      } catch {
        // Server failed — keep showing whatever is cached; add welcome if nothing
        setMessages(prev => prev.length ? prev : [welcomeMsg()]);
      } finally {
        setHistoryLoading(false);
      }
    };
    loadHistory();
  }, []);

  // ── Load pinned on open ────────────────────────────────────────────────────
  useEffect(() => {
    if (!pinnedOpen) return;
    axios.get(`${API}/chatbot/pinned`, auth()).then(r => setPinned(r.data)).catch(() => {});
  }, [pinnedOpen]);

  const welcomeMsg = () => ({
    id: "welcome",
    role: "assistant",
    content: "Hey! I'm Chanakya — think of me as that one friend who actually understands money.\n\nJust tell me what's on your mind:\n*spent 500 swiggy* · *salary 89000* · *can I afford a trip?*",
    timestamp: new Date().toISOString(),
  });

  const onboardingMsg = () => ({
    id: "onboarding",
    role: "assistant",
    content: "Hey! I'm *Chanakya* — your personal money advisor 👋\n\nTo get you a proper *financial health score* and smart advice, I need to know a few things:\n\n1️⃣ What's your monthly income? *(e.g. salary 85000)*\n2️⃣ Any active EMIs? *(e.g. home loan 18000/month)*\n3️⃣ What do you spend most on?\n\nOnce you share this, I'll give you a real picture of your finances.",
    timestamp: new Date().toISOString(),
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // ── Load older messages on scroll up ──────────────────────────────────────
  const handleScroll = useCallback(async () => {
    if (!scrollRef.current || !hasMore || loadingMore) return;
    if (scrollRef.current.scrollTop > 80) return;
    const oldest = oldestIdRef.current;
    if (!oldest || oldest === "welcome") return;
    setLoadingMore(true);
    try {
      const res = await axios.get(`${API}/chatbot/history?limit=20&before=${oldest}`, auth());
      const older = res.data;
      if (older.length < 20) setHasMore(false);
      if (older.length > 0) oldestIdRef.current = older[0].id;
      const prevH = scrollRef.current.scrollHeight;
      setMessages(prev => [...older, ...prev]);
      // Keep scroll position stable
      requestAnimationFrame(() => {
        if (scrollRef.current)
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight - prevH;
      });
    } catch {} finally { setLoadingMore(false); }
  }, [hasMore, loadingMore]);

  // ── Voice ──────────────────────────────────────────────────────────────────
  const [isVoiceInput, setIsVoiceInput] = useState(false);
  const { listening, toggle: toggleVoice, supported: voiceSupported } = useSpeechRecognition(
    useCallback((t) => { setInput(t); setIsVoiceInput(true); setTimeout(() => sendMessageRef.current?.(t), 300); }, [])
  );


  // ── Search ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!searchMode || !searchQuery.trim()) { setSearchResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/chatbot/search?q=${encodeURIComponent(searchQuery)}`, auth());
        setSearchResults(res.data);
      } catch { setSearchResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, searchMode]);

  // ── Pin / Unpin ────────────────────────────────────────────────────────────
  const togglePin = async (msg) => {
    if (!msg.id || msg.id === "welcome") return;
    try {
      const res = await axios.put(`${API}/chatbot/message/${msg.id}/pin`, {}, auth());
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, pinned: res.data.pinned } : m));
      setPinned(prev =>
        res.data.pinned ? [{ ...msg, pinned: true }, ...prev] : prev.filter(p => p.id !== msg.id)
      );
    } catch {}
  };

  // ── Delete message ─────────────────────────────────────────────────────────
  const deleteMessage = async (id) => {
    if (!id || id === "welcome") return;
    try {
      await axios.delete(`${API}/chatbot/message/${id}`, auth());
      setMessages(prev => prev.filter(m => m.id !== id));
    } catch {}
  };

  // ── Clear chat ─────────────────────────────────────────────────────────────
  const clearChat = async () => {
    const typed = await confirm({ title: 'Delete all Chanakya messages?', body: 'This permanently deletes your entire chat history with Chanakya.', prompt: true, placeholder: 'Type DELETE MY CHAT to confirm' });
    if (!typed || typed.trim().toUpperCase() !== "DELETE MY CHAT") return;
    try {
      await axios.delete(`${API}/chatbot/history`, auth());
      localStorage.removeItem(CHAT_CACHE_KEY);
      setMessages([welcomeMsg()]);
      setPinned([]);
    } catch {}
  };

  // ── Prompt optimizer ─────────────────────────────────────────────────────────
  // Strips filler words, injects the user's known financial context so Chanakya
  // gets precise data without the user having to type it every time.
  const optimizeInput = () => {
    const raw = input.trim();
    if (!raw) return;

    // Strip common filler phrases
    const FILLERS = /\b(basically|so\s+like|so\s+basically|umm+|uh+|like|you know|I mean|actually|literally|just|kind of|sort of|hey\s+(chanakya|there)?|hi\s+(chanakya|there)?|hello)\b/gi;
    let clean = raw.replace(FILLERS, '').replace(/\s{2,}/g, ' ').trim();
    // Capitalize first character
    clean = clean.charAt(0).toUpperCase() + clean.slice(1);

    // Build financial context from the brief snapshot (if loaded)
    const snap = brief?.snapshot;
    const ctx = [];
    if (snap?.income_this_month > 0)  ctx.push(`Income ₹${snap.income_this_month.toLocaleString('en-IN')}/mo`);
    if (snap?.spent_this_month > 0)   ctx.push(`Spent ₹${snap.spent_this_month.toLocaleString('en-IN')} this month`);
    if (snap?.active_emis > 0)        ctx.push(`${snap.active_emis} active EMI${snap.active_emis > 1 ? 's' : ''}`);
    if (snap?.active_goals > 0)       ctx.push(`${snap.active_goals} savings goal${snap.active_goals > 1 ? 's' : ''}`);
    if (brief?.score)                 ctx.push(`financial score ${brief.score}/100`);

    const optimized = ctx.length > 0
      ? `[My profile: ${ctx.join(' · ')}]\n\n${clean}`
      : clean;

    if (optimized === raw) return; // nothing changed
    setPreOptInput(raw);           // save original for undo
    setInput(optimized);
    inputRef.current?.focus();
  };

  // ── Send ───────────────────────────────────────────────────────────────────
  const sendMessage = async (text, overridePending = null) => {
    const msg = (text || input).trim();
    if (!msg || isLoading) return;

    // ── Client-side sensitive data guard — block BEFORE server call ──────────
    const sensitiveLabel = detectSensitiveData(msg);
    if (sensitiveLabel) {
      const warningMsg = {
        id: `sensitive-${Date.now()}`,
        role: "assistant",
        content: `🔒 Your message appears to contain a **${sensitiveLabel}**.\n\nFor your safety, this message has been blocked and will never reach our AI.\n\nBudget Mantra will **never** need your card numbers, PINs, OTPs, passwords, Aadhaar, PAN, or banking credentials. Please never share these in chat.\n\nIf you want to log something, just describe it in plain words — e.g. *"credit card bill ₹4,500"* — and I'll handle it. 🙏`,
        timestamp: new Date().toISOString(),
        isError: false,
      };
      setMessages(prev => [...prev, warningMsg]);
      setInput("");
      return;
    }

    const userMsg = {
      id: `tmp-${Date.now()}`,
      role: "user",
      content: msg,
      timestamp: new Date().toISOString(),
      reply_to: replyTo?.id || null,
      _replyToData: replyTo,
    };
    setMessages(prev => [...prev, userMsg]);
    setInput(""); setPreOptInput(null); setReplyTo(null); setActiveAction(null);
    setIsLoading(true);

    try {
      const voiceFlag = isVoiceInput;
      setIsVoiceInput(false);
      const res = await axios.post(`${API}/chatbot`, {
        message: msg,
        conversation_history: [],
        pending_entries: overridePending || pendingEntries || [],
        pending_delete: pendingDelete || null,
        pending_edit: pendingEdit || null,
        reply_to: replyTo?.id || null,
        is_voice: voiceFlag,
      }, auth());

      const data = res.data;

      // Replace temp user msg with DB id if returned
      if (data.user_msg_id) {
        setMessages(prev => prev.map(m => m.id === userMsg.id ? { ...m, id: data.user_msg_id } : m));
      }

      const assistantMsg = {
        id: data.asst_msg_id || `tmp-asst-${Date.now()}`,
        role: "assistant",
        content: data.response,
        timestamp: new Date().toISOString(),
        pending_entries: data.pending_entries || null,
        pending_delete: data.pending_delete || null,
        pending_edit: data.pending_edit || null,
        messagesLeft: data.messages_left,
        thinking_trace: data.thinking_trace || null,
        input_tokens: data.input_tokens || 0,
        output_tokens: data.output_tokens || 0,
      };
      addTokens(data.input_tokens || 0, data.output_tokens || 0);
      setMessages(prev => {
        const next = [...prev, assistantMsg];
        try { localStorage.setItem(CHAT_CACHE_KEY, JSON.stringify(next.slice(-20))); } catch { /* quota */ }
        return next;
      });

      if (data.pending_entries?.length) {
        setPendingEntries(data.pending_entries);
        setPendingDelete(null); setPendingEdit(null);
      } else if (data.pending_delete) {
        setPendingDelete(data.pending_delete);
        setPendingEntries(null); setPendingEdit(null);
      } else if (data.pending_edit) {
        setPendingEdit(data.pending_edit);
        setPendingEntries(null); setPendingDelete(null);
      } else {
        setPendingEntries(null); setPendingDelete(null); setPendingEdit(null);
        if ((data.layer === 1 && data.status === "success") ||
            data.status === "deleted" || data.status === "updated") {
          // Bust stale caches so pages show fresh data immediately on next mount
          const BUST_KEYS = [
            "bm_budget_cache", "bm_transactions_cache", "bm_income_cache",
            "bm_emis_cache", "bm_goals_cache", "bm_investments_cache",
            "bm_hand_loans_cache", "bm_recurring_cache",
          ];
          BUST_KEYS.forEach(k => localStorage.removeItem(k));
          window.dispatchEvent(new CustomEvent("chanakya-logged"));
        }
      }
    } catch (err) {
      const status = err?.response?.status;
      const content = status === 429
        ? "We've hit today's message limit. Come back tomorrow!"
        : "Something went sideways on my end. Your data is safe — just try again.";
      setMessages(prev => [...prev, { id: `err-${Date.now()}`, role: "assistant", content, timestamp: new Date().toISOString(), isError: true }]);
      setPendingEntries(null);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  sendMessageRef.current = sendMessage;

  const displayMsgs = searchMode && searchResults !== null ? searchResults : messages;
  const pinnedCount = messages.filter(m => m.pinned).length;

  return (
    <>
      {infoOpen && <InfoModal onClose={() => setInfoOpen(false)} />}
      <div
        ref={containerRef}
        className="fixed top-14 bottom-16 left-0 right-0 md:relative md:top-auto md:bottom-auto md:left-auto md:right-auto md:h-[calc(100dvh-56px)] flex flex-col overflow-hidden py-0 md:py-6"
        style={{ background: "linear-gradient(180deg,#1a0f05 0%,#2a1508 60%,#1a0f05 100%)" }}
      >
        <div className="flex-1 flex flex-col max-w-2xl w-full mx-auto min-h-0">

          {/* ── Top bar ───────────────────────────────────────────────────── */}
          <div className="flex items-center gap-3 px-4 py-3 shrink-0">
            {searchMode ? (
              <>
                <button onClick={() => { setSearchMode(false); setSearchQuery(""); setSearchResults(null); }}
                  className="text-stone-400 hover:text-white transition-colors">
                  <X size={20} />
                </button>
                <input
                  autoFocus
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search messages..."
                  className="flex-1 bg-stone-800/80 text-stone-100 placeholder-stone-500 text-sm px-4 py-2 rounded-xl outline-none border border-stone-700/50"
                />
              </>
            ) : (
              <>
                <ChanakyaDP size={40} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <h1 className="text-base font-bold text-stone-100 leading-tight">Chanakya</h1>
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                  </div>
                  <p className="text-[11px] text-stone-400 mt-0.5">Your AI financial advisor</p>
                </div>
                <div className="flex items-center gap-1">
                  {/* AI on/off switch */}
                  <button
                    onClick={toggleAI}
                    disabled={prefSaving}
                    title="Turn AI on/off"
                    className={`flex items-center gap-1.5 px-2 h-8 rounded-xl text-[11px] font-semibold transition-colors disabled:opacity-50 ${
                      aiEnabled
                        ? 'bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25'
                        : 'bg-stone-800 text-stone-400 hover:bg-stone-700'
                    }`}
                  >
                    <span className={`w-6 h-3.5 rounded-full relative transition-colors ${aiEnabled ? 'bg-emerald-500' : 'bg-stone-600'}`}>
                      <span className={`absolute top-0.5 w-2.5 h-2.5 rounded-full bg-white transition-all ${aiEnabled ? 'left-3' : 'left-0.5'}`} />
                    </span>
                    AI
                  </button>

                  {/* Language toggle */}
                  <button
                    onClick={toggleLang}
                    disabled={prefSaving}
                    title="Switch chat language"
                    className="px-2 h-8 rounded-xl bg-stone-800 hover:bg-stone-700 text-[11px] font-semibold text-stone-200 transition-colors disabled:opacity-50"
                  >
                    {lang === 'english' ? 'English' : 'Hinglish'}
                  </button>

                  {/* Info icon */}
                  <button
                    onClick={() => setInfoOpen(true)}
                    title="Usage & data info"
                    className="w-8 h-8 rounded-xl hover:bg-stone-800 flex items-center justify-center text-stone-400 hover:text-amber-300 transition-colors"
                  >
                    <Info size={16} />
                  </button>

                  {pinnedCount > 0 && (
                    <button onClick={() => setPinnedOpen(o => !o)}
                      className="relative w-8 h-8 rounded-xl hover:bg-stone-800 flex items-center justify-center text-amber-400 transition-colors">
                      <Pin size={16} />
                      <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-orange-500 text-white text-[9px] font-bold flex items-center justify-center">
                        {pinnedCount}
                      </span>
                    </button>
                  )}
                  <button onClick={() => setSearchMode(true)}
                    className="w-8 h-8 rounded-xl hover:bg-stone-800 flex items-center justify-center text-stone-400 hover:text-stone-200 transition-colors">
                    <Search size={16} />
                  </button>
                  <button onClick={clearChat}
                    className="w-8 h-8 rounded-xl hover:bg-stone-800 flex items-center justify-center text-stone-400 hover:text-rose-400 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              </>
            )}
          </div>

          {/* ── Chat / Updates tab bar ───────────────────────────────────── */}
          {!searchMode && (
            <div className="flex mx-4 mb-1 gap-1 shrink-0">
              {[
                { id: 'chat',    label: 'Chat',    icon: <Sparkles size={13} /> },
                { id: 'updates', label: 'Updates', icon: <Bell size={13} /> },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); if (tab.id === 'updates') { setNotifsLoading(true); fetchNotifs(); } }}
                  className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${
                    activeTab === tab.id
                      ? 'bg-amber-700/40 text-amber-300'
                      : 'text-stone-500 hover:text-stone-300 hover:bg-stone-800/60'
                  }`}
                >
                  {tab.icon}{tab.label}
                  {tab.id === 'updates' && unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-orange-500 text-white text-[9px] font-bold flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* ── Updates panel ────────────────────────────────────────────── */}
          {activeTab === 'updates' && !searchMode && (
            <UpdatesPanel
              notifs={notifs}
              loading={notifsLoading}
              onRead={async (id) => {
                await axios.post(`${API}/notifications/mark-read`, { id }, auth()).catch(() => {});
                fetchNotifs();
              }}
              onMarkAll={async () => {
                await axios.post(`${API}/notifications/mark-read`, { kind: 'all' }, auth()).catch(() => {});
                fetchNotifs();
              }}
            />
          )}

          {/* ── Pinned panel ─────────────────────────────────────────────── */}
          {pinnedOpen && pinned.length > 0 && (
            <div className="mx-4 mb-2 bg-amber-950/40 border border-amber-800/40 rounded-2xl p-3 shrink-0 max-h-36 overflow-y-auto">
              <p className="text-xs font-semibold text-amber-400 mb-2 flex items-center gap-1">
                <Pin size={11} /> Pinned
              </p>
              {pinned.map(m => (
                <div key={m.id} className="text-xs text-stone-300 py-1 border-b border-stone-700/30 last:border-0 line-clamp-1">
                  {m.content}
                </div>
              ))}
            </div>
          )}

          {/* ── Search label ─────────────────────────────────────────────── */}
          {searchMode && searchResults !== null && (
            <div className="px-4 mb-1 shrink-0">
              <p className="text-xs text-stone-500">{searchResults.length} result{searchResults.length !== 1 ? 's' : ''}</p>
            </div>
          )}

          {/* ── Daily Brief card ─────────────────────────────────────────── */}
          {!searchMode && activeTab === 'chat' && <DailyBrief />}

          {/* ── Messages ─────────────────────────────────────────────────── */}
          {/* Session token stats bar */}
          {sessionTokens.input + sessionTokens.output > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '5px 16px', borderBottom: '0.5px solid #292524', fontFamily: 'monospace', fontSize: 11, color: '#78716c', background: '#1c1917' }}>
              <span style={{ color: '#44403c' }}>Session</span>
              <span style={{ color: '#a8a29e' }}>{((sessionTokens.input + sessionTokens.output) / 1000).toFixed(1)}K tokens</span>
              <span style={{ color: sessionTokens.input + sessionTokens.output > 50000 ? '#f59e0b' : '#10b981' }}>
                ₹{(((sessionTokens.input / 1_000_000) * 3.00 + (sessionTokens.output / 1_000_000) * 15.00) * 84).toFixed(3)}
              </span>
              <span style={{ flex: 1 }} />
              <span style={{ color: '#44403c' }}>
                {sessionTokens.output > 0 ? Math.round((sessionTokens.output / (sessionTokens.input + sessionTokens.output)) * 100) : 0}% output
              </span>
            </div>
          )}

          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className={`flex-1 overflow-y-auto scrollbar-none space-y-2 px-4 pb-2 min-h-0 scroll-smooth ${activeTab === 'updates' ? 'hidden' : ''}`}
          >
            {loadingMore && (
              <div className="text-center py-2">
                <span className="text-xs text-stone-500">Loading older messages…</span>
              </div>
            )}
            {historyLoading ? (
              <div className="flex justify-center pt-8">
                <TypingDots />
              </div>
            ) : (
              displayMsgs.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  msg={msg}
                  allMessages={messages}
                  onReply={(m) => { setReplyTo(m); inputRef.current?.focus(); }}
                  onPin={togglePin}
                  onDelete={deleteMessage}
                  pendingEntries={pendingEntries}
                  onConfirm={() => { const p = pendingEntries; setPendingEntries(null); sendMessage("yes", p); }}
                  onReject={() => { setPendingEntries(null); setMessages(prev => [...prev, { id: `fix-${Date.now()}`, role: "assistant", content: "Sure — tell me what to change and I'll fix it.", timestamp: new Date().toISOString() }]); }}
                  pendingDelete={pendingDelete}
                  onConfirmDelete={() => { setPendingDelete(null); sendMessage("yes, delete it"); }}
                  onCancelDelete={() => { setPendingDelete(null); sendMessage("no, keep it"); }}
                  pendingEdit={pendingEdit}
                  onConfirmEdit={() => { setPendingEdit(null); sendMessage("yes, update it"); }}
                  onCancelEdit={() => { setPendingEdit(null); sendMessage("no, cancel"); }}
                  isLastAsst={msg.role === "assistant" && msg.id === [...displayMsgs].reverse().find(m => m.role === "assistant")?.id}
                />
              ))
            )}
            {isLoading && (
              <div className="flex gap-2.5">
                <ChanakyaDP size={28} />
                <div className="bg-stone-800/90 border border-stone-700/60 rounded-2xl rounded-tl-sm px-4 py-2.5">
                  <TypingDots />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* ── Quick action chips — always visible ──────────────────────── */}
          <div className="px-3 pb-1 pt-0.5 shrink-0 flex gap-2 overflow-x-auto scrollbar-none">
            {QUICK_ACTIONS.map(a => (
              <button
                key={a.key}
                onClick={() => setActiveAction(a.key)}
                style={{ borderColor: "rgba(255,255,255,0.1)", color: "#a8a29e" }}
                className="text-xs px-3 py-1.5 rounded-full border bg-stone-800/70 whitespace-nowrap shrink-0 flex items-center gap-1.5 active:scale-95 transition-all"
              >
                <a.icon size={11} style={{ color: a.color }} />
                {a.label}
              </button>
            ))}
          </div>

          {/* ── QuickComposer modal overlay — no layout shift ────────────── */}
          {activeAction && (
            <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: "rgba(0,0,0,0.55)" }} onClick={() => setActiveAction(null)}>
              <div className="w-full max-w-2xl" onClick={e => e.stopPropagation()}>
                <QuickComposer
                  action={activeAction}
                  onSend={(msg) => { sendMessage(msg); }}
                  onClose={() => setActiveAction(null)}
                />
              </div>
            </div>
          )}

          {/* ── Reply-to preview ─────────────────────────────────────────── */}
          {replyTo && (
            <div className="mx-4 mb-1 px-3 py-2 bg-stone-800/80 border-l-2 border-amber-500 rounded-r-xl flex items-center gap-2 shrink-0">
              <Reply size={12} className="text-amber-400 shrink-0" />
              <p className="text-xs text-stone-400 truncate flex-1">{replyTo.content}</p>
              <button onClick={() => setReplyTo(null)} className="text-stone-500 hover:text-stone-300">
                <X size={14} />
              </button>
            </div>
          )}

          {/* ── Voice waveform (shown while listening) ───────────────────── */}
          {listening && voiceSupported && (
            <div className="px-4 py-3 shrink-0 flex items-center gap-3 border-t border-stone-700/50 bg-stone-900">
              <div className="flex-1 flex items-center justify-center gap-[5px] h-10">
                {[0.3,0.6,1,0.7,0.45,0.9,0.55,0.8,0.4,0.65].map((delay, i) => (
                  <div key={i} className="bm-voice-bar"
                    style={{
                      animationDelay: `${delay * 0.4}s`,
                      backgroundColor: i % 2 === 0 ? '#f97316' : '#fbbf24',
                    }} />
                ))}
              </div>
              <span className="text-xs font-semibold text-orange-400 shrink-0">Listening…</span>
              <button onClick={toggleVoice}
                className="w-9 h-9 rounded-xl border border-orange-500/40 bg-orange-500/10 text-orange-400 flex items-center justify-center hover:bg-orange-500/20 transition-all active:scale-90 shrink-0">
                <MicOff size={15} />
              </button>
            </div>
          )}

          {/* ── Text input bar ───────────────────────────────────────────── */}
          {!listening && activeTab === 'chat' && (
            <div className="px-3 pb-3 pt-1 shrink-0" style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}>
              <div className="flex items-end gap-2 w-full min-w-0">
                <div className="flex-1 min-w-0 relative">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={e => { setInput(e.target.value.slice(0, 500)); setIsVoiceInput(false); setPreOptInput(null); }}
                    onKeyDown={e => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    placeholder="Ask Chanakya anything…"
                    rows={1}
                    maxLength={500}
                    className="w-full resize-none bg-stone-800/90 border border-stone-700/50 text-stone-100 placeholder-stone-500 text-sm rounded-2xl px-4 py-2.5 outline-none focus:border-amber-600/50 transition-colors max-h-28"
                    style={{ lineHeight: "1.5" }}
                    onInput={e => { e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 112) + "px"; }}
                  />
                  {/* Optimized indicator + undo */}
                  {preOptInput && (
                    <div className="absolute -top-6 left-0 flex items-center gap-2">
                      <span className="text-[10px] font-bold text-amber-400 flex items-center gap-1">
                        <Zap size={9} /> Optimized
                      </span>
                      <button
                        onClick={() => { setInput(preOptInput); setPreOptInput(null); }}
                        className="text-[10px] text-stone-500 hover:text-stone-300 underline transition-colors"
                      >
                        undo
                      </button>
                    </div>
                  )}
                  {input.length > 400 && (
                    <span className={`absolute bottom-1 right-3 text-[10px] ${input.length >= 500 ? "text-rose-400" : "text-stone-500"}`}>
                      {input.length}/500
                    </span>
                  )}
                </div>

                {/* ⚡ Prompt optimizer — enriches query with financial context */}
                {!preOptInput && (
                  <button
                    onClick={input.trim().length > 8 ? optimizeInput : undefined}
                    title="Optimize prompt — adds your financial context automatically"
                    className={`w-10 h-10 rounded-xl border flex items-center justify-center shrink-0 transition-all ${
                      input.trim().length > 8
                        ? 'border-amber-600/40 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 hover:text-amber-300 active:scale-90 cursor-pointer'
                        : 'border-stone-700/30 bg-transparent text-stone-700 cursor-default'
                    }`}
                  >
                    <Zap size={15} />
                  </button>
                )}

                {/* Voice — tap to start, waveform bar replaces input while listening */}
                {voiceSupported && (
                  <button onClick={toggleVoice}
                    className="w-10 h-10 rounded-xl border border-stone-700/50 bg-stone-800 text-stone-400 hover:text-stone-200 flex items-center justify-center shrink-0 transition-all active:scale-90">
                    <Mic size={16} />
                  </button>
                )}

                {/* Send */}
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || isLoading}
                  className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center shrink-0 disabled:opacity-40 active:scale-95 transition-all shadow-md shadow-orange-500/30">
                  <Send size={15} className="text-white" />
                </button>
              </div>
            </div>
          )}

          {/* Safe-area spacer when composer is shown */}
          {activeAction && (
            <div style={{ height: "max(12px, env(safe-area-inset-bottom))", flexShrink: 0 }} />
          )}

        </div>
      </div>
    </>
  );
};

// ── Updates panel ─────────────────────────────────────────────────────────────
const NOTIF_META = {
  payday_nudge:         { icon: '💸', label: 'Payday',    color: '#10b981' },
  goal_milestone:       { icon: '🎯', label: 'Goal',      color: '#8b5cf6' },
  budget_alert:         { icon: '⚠️', label: 'Budget',    color: '#ef4444' },
  emi_reminder:         { icon: '💳', label: 'EMI',       color: '#6366f1' },
  weekly_digest:        { icon: '📊', label: 'Digest',    color: '#3b82f6' },
  monthly_summary:      { icon: '📅', label: 'Summary',   color: '#f97316' },
  import_done:          { icon: '📥', label: 'Import',    color: '#3b82f6' },
  smart_buy_ready:      { icon: '🛍️', label: 'Smart Buy', color: '#f97316' },
  trip_itinerary_ready: { icon: '✈️', label: 'Trip',      color: '#0891b2' },
  system:               { icon: '✨', label: 'Chanakya',  color: '#f97316' },
};

function fmtNotifTime(iso) {
  const d = new Date(iso), now = new Date();
  const diffH = Math.floor((now - d) / 3600000);
  const diffD = Math.floor(diffH / 24);
  if (diffH < 1) return 'Just now';
  if (diffH < 24) return `${diffH}h ago`;
  if (diffD < 7) return `${diffD}d ago`;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function UpdatesPanel({ notifs, loading, onRead, onMarkAll }) {
  const hasUnread = notifs.some(n => !n.read);
  if (loading && notifs.length === 0) {
    return <div className="flex-1 flex items-center justify-center text-stone-500 text-sm">Loading…</div>;
  }
  if (notifs.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6">
        <div className="w-14 h-14 rounded-2xl bg-stone-800 flex items-center justify-center text-2xl">🔔</div>
        <p className="text-stone-400 text-sm font-medium">No updates yet</p>
        <p className="text-stone-600 text-xs">Chanakya will send alerts here when something needs your attention.</p>
      </div>
    );
  }
  return (
    <div className="flex-1 overflow-y-auto scrollbar-none min-h-0">
      {hasUnread && (
        <div className="flex justify-end px-4 py-1.5 shrink-0">
          <button onClick={onMarkAll} className="text-xs text-amber-400 hover:text-amber-300 font-semibold transition-colors">
            Mark all read
          </button>
        </div>
      )}
      <div className="px-3 pb-4 space-y-2">
        {notifs.map(n => {
          const meta = NOTIF_META[n.kind] || NOTIF_META[n.type] || { icon: '🔔', label: 'Alert', color: '#78716c' };
          return (
            <div
              key={n.id}
              onClick={() => !n.read && onRead(n.id)}
              className={`flex gap-3 p-3 rounded-2xl border transition-colors cursor-pointer ${
                n.read
                  ? 'bg-stone-900/40 border-stone-800/40'
                  : 'bg-stone-800/60 border-stone-700/60'
              }`}
            >
              <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 text-base"
                style={{ background: meta.color + '22' }}>
                {meta.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: meta.color }}>
                    {meta.label}
                  </span>
                  <span className="text-[10px] text-stone-500 shrink-0">{fmtNotifTime(n.timestamp || n.created_at)}</span>
                </div>
                {n.title && <p className="text-xs font-semibold text-stone-200 mb-0.5">{n.title}</p>}
                <p className="text-xs text-stone-400 line-clamp-3">{n.body || n.content}</p>
              </div>
              {!n.read && <div className="w-2 h-2 rounded-full bg-orange-400 shrink-0 mt-1.5" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── System notification avatar ─────────────────────────────────────────────────
function SystemAvatar({ size = 28 }) {
  return (
    <div
      style={{
        width: size, height: size, borderRadius: Math.round(size * 0.28),
        background: "linear-gradient(135deg,#78350f 0%,#b45309 100%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, boxShadow: "0 2px 6px rgba(180,83,9,0.4)",
      }}
    >
      <Bell size={Math.round(size * 0.44)} color="#fef3c7" />
    </div>
  );
}

// ── Message bubble ────────────────────────────────────────────────────────────
function MessageBubble({ msg, allMessages, onReply, onPin, onDelete, pendingEntries, onConfirm, onReject, pendingDelete, onConfirmDelete, onCancelDelete, pendingEdit, onConfirmEdit, onCancelEdit, isLastAsst }) {
  const isUser   = msg.role === "user";
  const isSystem = msg.source === "system";
  const replyMsg = msg.reply_to ? allMessages.find(m => m.id === msg.reply_to) : null;

  return (
    <div className={`flex gap-2 group ${isUser ? "flex-row-reverse" : ""}`}>
      {/* Avatar */}
      {isUser
        ? <div className="w-7 h-7 rounded-xl bg-orange-500 flex items-center justify-center shrink-0 mt-0.5">
            <Sparkles size={13} className="text-white" />
          </div>
        : isSystem
          ? <SystemAvatar size={28} />
          : <ChanakyaDP size={28} />
      }

      {/* Bubble + actions */}
      <div className={`flex flex-col max-w-[80%] ${isUser ? "items-end" : "items-start"}`}>
        {/* Replied-to quote */}
        {replyMsg && (
          <div className="mb-1 px-3 py-1.5 bg-stone-900/60 border-l-2 border-amber-500 rounded-r-xl max-w-full">
            <p className="text-xs text-stone-400 truncate">{replyMsg.content}</p>
          </div>
        )}

        {/* Attachment */}
        {msg.attachment && (
          <div className="mb-1">
            {msg.attachment.type === "image" ? (
              <img
                src={`data:${msg.attachment.mime};base64,${msg.attachment.data}`}
                alt={msg.attachment.name}
                className="max-w-[220px] rounded-xl border border-stone-700/50"
              />
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 bg-stone-800 border border-stone-700/50 rounded-xl">
                <FileText size={16} className="text-rose-400 shrink-0" />
                <span className="text-xs text-stone-300 truncate max-w-[150px]">{msg.attachment.name}</span>
              </div>
            )}
          </div>
        )}

        <div className="relative">
          {/* Bubble */}
          <div className={`px-4 py-2.5 rounded-2xl text-[13.5px] leading-relaxed ${
            isUser
              ? "bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-tr-sm"
              : msg.isError
                ? "bg-red-950/60 text-red-300 border border-red-800/50 rounded-tl-sm"
                : isSystem
                  ? "bg-amber-950/50 text-amber-100 border border-amber-800/40 rounded-tl-sm"
                  : "bg-stone-800/90 text-stone-100 border border-stone-700/60 rounded-tl-sm"
          }`}>
            {renderMessage(msg.content)}
            {/* Pin indicator */}
            {msg.pinned && <Pin size={10} className="inline ml-1.5 text-amber-400 opacity-70" />}
          </div>

          {/* System badge */}
          {isSystem && (
            <div className="absolute -top-2 left-2 flex items-center gap-1 bg-amber-800/80 rounded-full px-1.5 py-0.5">
              <Bell size={8} className="text-amber-200" />
              <span className="text-[9px] text-amber-200 font-semibold tracking-wide uppercase">Auto</span>
            </div>
          )}

          {/* Hover action row — hidden for system messages */}
          {!isSystem && (
            <div className={`absolute ${isUser ? "right-full mr-1" : "left-full ml-1"} top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-0.5 bg-stone-900/90 border border-stone-700/50 rounded-xl px-1 py-0.5`}>
              <ActionBtn icon={Reply}    onClick={() => onReply(msg)} title="Reply" />
              <ActionBtn icon={msg.pinned ? PinOff : Pin} onClick={() => onPin(msg)} title={msg.pinned ? "Unpin" : "Pin"} className="text-amber-400" />
              <ActionBtn icon={Trash2}   onClick={() => onDelete(msg.id)} title="Delete" className="text-rose-400" />
            </div>
          )}
        </div>

        {/* Confirm chips — log entries */}
        {isLastAsst && pendingEntries?.length > 0 && (
          <div className="flex gap-2 mt-2">
            <button onClick={onConfirm} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl transition-all">
              <Check size={12} /> Yes, log it
            </button>
            <button onClick={onReject} className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-700 hover:bg-stone-600 text-stone-200 text-xs font-semibold rounded-xl transition-all">
              <X size={12} /> Fix it
            </button>
          </div>
        )}

        {/* Confirm chips — delete */}
        {isLastAsst && pendingDelete && (
          <div className="flex gap-2 mt-2">
            <button onClick={onConfirmDelete} className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold rounded-xl transition-all">
              <Trash2 size={12} /> Yes, delete
            </button>
            <button onClick={onCancelDelete} className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-700 hover:bg-stone-600 text-stone-200 text-xs font-semibold rounded-xl transition-all">
              <X size={12} /> No, keep it
            </button>
          </div>
        )}

        {/* Confirm chips — edit */}
        {isLastAsst && pendingEdit && (
          <div className="flex gap-2 mt-2">
            <button onClick={onConfirmEdit} className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold rounded-xl transition-all">
              <Check size={12} /> Yes, update
            </button>
            <button onClick={onCancelEdit} className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-700 hover:bg-stone-600 text-stone-200 text-xs font-semibold rounded-xl transition-all">
              <X size={12} /> Cancel
            </button>
          </div>
        )}

        {/* Timestamp */}
        <span className="text-[10px] text-stone-600 mt-0.5 px-1">
          {msg.timestamp ? new Date(
            /Z$|[+-]\d{2}:\d{2}$/.test(msg.timestamp) ? msg.timestamp : msg.timestamp + 'Z'
          ).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ""}
        </span>
      </div>
    </div>
  );
}

function ActionBtn({ icon: Icon, onClick, title, className = "text-stone-400" }) {
  return (
    <button onClick={onClick} title={title}
      className={`w-6 h-6 rounded-lg flex items-center justify-center hover:bg-stone-700 transition-colors ${className}`}>
      <Icon size={12} />
    </button>
  );
}

export default Chatbot;
